import _get from 'lodash/get'
import * as z from 'zod'

import { UPGRADES_MAP } from '@flows/configs/contents/constants'
import type { ContentsNeoStepConfigParams } from '@flows/configs/contents/types'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import * as validators from '@flows/configs/shared/validators'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_ContentsPremiumBikeTheftNeodigitalDe } from '@flows/insurance/schemas'

const contents_bike_insured_sum = (params: ContentsNeoStepConfigParams) => {
  const bikeProductKey = UPGRADES_MAP.bikeProductKey[params.productKey]
  return {
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('bike_theft_insured_sum_amount', bikeProductKey)]:
        validators.createPreprocessedNumberSchema(
          BaseSchema_ContentsPremiumBikeTheftNeodigitalDe.shape.bike_theft_insured_sum_amount
            .innerType()
            .gte(500)
            .lte(5000)
        ),
      [params.helperFunctions.getHelperPath('insured_sum_confirmation', bikeProductKey)]:
        z.boolean(),
    }),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_contents:steps.contents.bike.insured_sum.title',
        },
      },
      {
        type: 'input',
        inputType: 'integer',
        name: params.helperFunctions.getParamPath('bike_theft_insured_sum_amount', bikeProductKey),
        label: {
          type: 'translatableText',
          translationKey: 'sharedFlows_contents:steps.contents.bike.insured_sum.input',
        },
        onValueChange: {
          type: 'dispatchedHook',
          callbackConfig: {
            name: 'hideErrorMessageHook',
            inputs: [],
            outputs: [],
          },
        },
      },
      {
        type: 'infoBox',
        contentConfiguration: [
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey: 'sharedFlows_contents:steps.contents.bike.insured_sum.info.text',
            },
          },
        ],
      },
      {
        type: 'text',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_contents:steps.contents.bike.insured_sum.confirmation',
        },
        condition: {
          type: 'value',
          valuePath: params.helperFunctions.getHelperPath(
            'insured_sum_show_confirmation',
            bikeProductKey
          ),
          expectedValue: true,
        },
      },
      {
        type: 'yesNo',
        shouldTrackValues: true,
        name: params.helperFunctions.getHelperPath('insured_sum_confirmation', bikeProductKey),
        condition: {
          type: 'value',
          valuePath: params.helperFunctions.getHelperPath(
            'insured_sum_show_confirmation',
            bikeProductKey
          ),
          expectedValue: true,
        },
        onValueChange: {
          type: 'dispatchedHook',
          callbackConfig: {
            name: 'hideErrorMessageHook',
            inputs: [],
            outputs: [],
          },
        },
      },
    ],
    subFlow: params.subFlow,
    onSubmit: {
      type: 'simpleHook',

      callback: (formValues: FlowValues) => {
        /**
         * This callback will update the input price of the bike
         * The price can't be below 500 and over 5000
         */
        if (
          params.helperFunctions.getProductConfigurationParameters(formValues, bikeProductKey) ===
          undefined
        ) {
          throw new Error(`Assertion: ${bikeProductKey} configuration data is undefined`)
        }

        const bikeInsuredSumResult = z.coerce
          .string()
          .safeParse(
            _get(
              formValues,
              params.helperFunctions.getParamPath('bike_theft_insured_sum_amount', bikeProductKey)
            )
          )

        const insured_sum_confirmation = _get(
          formValues,
          params.helperFunctions.getHelperPath('insured_sum_confirmation', bikeProductKey)
        )

        if (insured_sum_confirmation === false) {
          return {
            formValues: {
              [`product_keys.${bikeProductKey}`]: false,
              [params.helperFunctions.getHelperPath('insured_sum_confirmation', bikeProductKey)]:
                null,
            },
          }
        }

        if (!bikeInsuredSumResult.success) {
          throw new Error('Assertion: Bike insured sum is undefined')
        }

        const bikeInsuredSum = bikeInsuredSumResult.data

        if (bikeInsuredSum === '' || Number.parseInt(bikeInsuredSum) < 500) {
          return {
            formValues: {
              [params.helperFunctions.getHelperPath(
                'insured_sum_show_confirmation',
                bikeProductKey
              )]: true,
              [params.helperFunctions.getParamPath(
                'bike_theft_insured_sum_amount',
                bikeProductKey
              )]: '500',
            },
            error: {
              type: 'translatableText',
              translationKey: 'sharedFlows_contents:steps.contents.bikes.errors.insured_sum',
            },
          }
        }

        if (Number.parseInt(bikeInsuredSum) > 5000) {
          return {
            formValues: {
              [params.helperFunctions.getHelperPath(
                'insured_sum_show_confirmation',
                bikeProductKey
              )]: true,
              [params.helperFunctions.getParamPath(
                'bike_theft_insured_sum_amount',
                bikeProductKey
              )]: '5000',
            },
            error: {
              type: 'translatableText',
              translationKey: 'sharedFlows_contents:steps.contents.bikes.errors.insured_sum',
            },
          }
        }

        return {
          formValues: {
            [`product_keys.${bikeProductKey}`]: true,
            [params.helperFunctions.getHelperPath('insured_sum_show_confirmation', bikeProductKey)]:
              null,
          },
        }
      },
    },
    nextStep: 'wg_insured',
  } as const satisfies AnyStepConfiguration
}

export default contents_bike_insured_sum
